import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatQty, stockStatus, uuidv7, variantLabel, type StockStatus } from '@pos/core';
import { computeStock, rejectedMovementIds, type VariantRow, type VariantStock } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import type { Catalog, CatalogItem } from '../products/data.ts';
import type { StockContext } from './changes.ts';

/** Stock de cada variante en este dispositivo, en vivo. */
export function useStockMap(): Map<string, VariantStock> | undefined {
  const { business } = useSession();
  return useLiveQuery(async () => {
    const [levels, movements, rejected] = await Promise.all([
      db.stock_levels.where('business_id').equals(business.id).toArray(),
      db.stock_movements.where('business_id').equals(business.id).toArray(),
      rejectedMovementIds(db),
    ]);
    return computeStock(levels, movements, rejected);
  }, [business.id]);
}

export interface StockRow {
  item: CatalogItem;
  variant: VariantRow;
  /** "Remera · M / Negro" */
  label: string;
  qty: number;
  tracked: boolean;
  status: StockStatus;
  cost: number | undefined;
}

/** Una fila por variante (de productos activos o no), con su stock y estado. */
export function useStockRows(catalog: Catalog | undefined, stock: Map<string, VariantStock> | undefined): StockRow[] {
  return useMemo(() => {
    if (!catalog || !stock) return [];
    return catalog.items.flatMap((item) =>
      item.variants.map((variant): StockRow => {
        const s = stock.get(variant.id) ?? { qty: 0, tracked: false };
        const attrs = variantLabel(variant.attributes);
        return {
          item,
          variant,
          label: attrs ? `${item.product.name} · ${attrs}` : item.product.name,
          qty: s.qty,
          tracked: s.tracked,
          status: stockStatus(s.qty, variant.min_stock, s.tracked),
          cost: catalog.costs.get(variant.id),
        };
      }),
    );
  }, [catalog, stock]);
}

export function useStockContext(): () => StockContext {
  const { business, device, session } = useSession();
  return () => ({ businessId: business.id, deviceId: device.id, userId: session.user_id, now: new Date().toISOString(), newId: uuidv7 });
}

export const STATUS_STYLE: Record<StockStatus, { label: string; className: string }> = {
  negative: { label: 'Negativo', className: 'text-red-700' },
  out: { label: 'Sin stock', className: 'text-red-700' },
  low: { label: 'Stock bajo', className: 'text-amber-700' },
  ok: { label: 'OK', className: 'text-slate-900' },
  untracked: { label: 'Sin control', className: 'text-slate-400' },
};

export const isAlert = (s: StockStatus) => s === 'negative' || s === 'out' || s === 'low';

export function formatStock(row: Pick<StockRow, 'qty' | 'tracked' | 'item'>): string {
  return row.tracked ? formatQty(row.qty, row.item.product.unit_type) : '—';
}
